import { useState } from 'react'

import { withBase } from '../../lib/basePath'
import { brl, parseNumber, pct } from '../../lib/format'
import { color, radius } from '../../theme/tokens'
import type { ImpactPreview, Policy } from '../../lib/types'
import { Button, Check, Field, inputStyle, Shield, Toggle } from '../ui'

export interface SectionProps {
  policy: Policy
  patch(mutate: (draft: Policy) => void): void
  impact: ImpactPreview
  answerRate: number[]
}

export function SectionHeader({
  number,
  title,
  description,
  badge,
}: {
  number: number
  title: string
  description: string
  badge?: string
}) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '.09em',
            color: color.inkSubtle,
          }}
        >
          SEÇÃO {number}
        </span>
        {badge ? (
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              padding: '3px 9px',
              borderRadius: radius.pill,
              background: color.brandTint,
              color: color.brandInk,
            }}
          >
            {badge}
          </span>
        ) : null}
      </div>
      <h1 style={{ fontSize: 24, fontWeight: 600, margin: '0 0 4px', letterSpacing: '-.3px' }}>
        {title}
      </h1>
      <p
        style={{
          fontSize: 13,
          color: color.inkSubtle,
          margin: '0 0 20px',
          maxWidth: 680,
          lineHeight: 1.6,
        }}
      >
        {description}
      </p>
    </>
  )
}

// ------------------------------------------------------------------ 1 · Persona --

export function PersonaSection({ policy, patch }: SectionProps) {
  const { persona } = policy
  const formalityLabel =
    persona.formality < 35 ? 'informal' : persona.formality > 70 ? 'formal' : 'equilibrada'

  return (
    <div>
      <SectionHeader
        number={1}
        title="Persona do agente"
        description="Como a IA se apresenta e conduz o ritmo da conversa. Nada aqui é texto livre de prompt — cada controle vira uma linha do prompt compilado à direita."
      />
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div
          style={{
            flex: 1,
            minWidth: 360,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 14,
          }}
        >
          <Field label="Nome do agente">
            <input
              value={persona.name}
              onChange={(e) => patch((d) => { d.persona.name = e.target.value })}
              style={inputStyle}
            />
          </Field>

          <Field label="Voz (Realtime API)">
            <select
              value={persona.voice}
              onChange={(e) => patch((d) => { d.persona.voice = e.target.value })}
              style={inputStyle}
            >
              <option value="marin">marin · feminina, calorosa</option>
              <option value="cedar">cedar · feminina, neutra</option>
              <option value="alloy">alloy · neutra</option>
            </select>
          </Field>

          <Field label="Tratamento">
            <select
              value={persona.address}
              onChange={(e) =>
                patch((d) => { d.persona.address = e.target.value as Policy['persona']['address'] })
              }
              style={inputStyle}
            >
              <option value="senhor">senhor / senhora</option>
              <option value="voce">você</option>
              <option value="adapt">adaptativo ao cliente</option>
            </select>
          </Field>

          <Field label="Sotaque / regionalismo">
            <select
              value={persona.accent}
              onChange={(e) => patch((d) => { d.persona.accent = e.target.value })}
              style={inputStyle}
            >
              <option>Neutro brasileiro</option>
              <option>Paulista</option>
              <option>Nordestino</option>
            </select>
          </Field>

          <Field label="Velocidade de fala" hint={`${persona.speed.toFixed(2)}×`}>
            <input
              type="range"
              min={0.7}
              max={1.4}
              step={0.05}
              value={persona.speed}
              onChange={(e) =>
                patch((d) => { d.persona.speed = Number.parseFloat(e.target.value) })
              }
              style={{ accentColor: color.brand }}
            />
          </Field>

          <Field label="Formalidade" hint={formalityLabel}>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={persona.formality}
              onChange={(e) =>
                patch((d) => { d.persona.formality = Number.parseInt(e.target.value, 10) })
              }
              style={{ accentColor: color.brand }}
            />
          </Field>

          <Field label="Saudação de abertura" style={{ gridColumn: 'span 2' }}>
            <textarea
              value={persona.greeting}
              onChange={(e) => patch((d) => { d.persona.greeting = e.target.value })}
              rows={2}
              style={{ ...inputStyle, lineHeight: 1.5, resize: 'vertical' }}
            />
          </Field>

          <Field label="Personalidade e maneirismos" style={{ gridColumn: 'span 2' }}>
            <textarea
              value={persona.mannerisms}
              onChange={(e) => patch((d) => { d.persona.mannerisms = e.target.value })}
              rows={3}
              placeholder={
                'Como se comunicar — não o que oferecer. '
                + 'Ex.: use o nome da cliente com moderação; varie as confirmações.'
              }
              style={{ ...inputStyle, lineHeight: 1.5, resize: 'vertical' }}
            />
          </Field>

          {Object.entries(persona.segment_tones ?? {}).map(([segment, tone]) => (
            <Field
              key={segment}
              label={`Tom para clientes ${segment}`}
              style={{ gridColumn: 'span 2' }}
            >
              <textarea
                value={tone}
                onChange={(e) =>
                  patch((d) => { d.persona.segment_tones[segment] = e.target.value })
                }
                rows={2}
                placeholder="Na chamada, a IA aplica o tom do segmento do caso automaticamente."
                style={{ ...inputStyle, lineHeight: 1.5, resize: 'vertical' }}
              />
            </Field>
          ))}

          <Field label="Tempo máximo de chamada">
            <input
              value={`${persona.max_call_minutes} minutos`}
              onChange={(e) =>
                patch((d) => { d.persona.max_call_minutes = Math.max(1, parseNumber(e.target.value)) })
              }
              style={inputStyle}
            />
          </Field>

          <Field label="Política de silêncio">
            <input
              value={`sondar após ${persona.silence_probe_seconds} s`}
              onChange={(e) =>
                patch((d) => {
                  d.persona.silence_probe_seconds = Math.max(1, parseNumber(e.target.value))
                })
              }
              style={inputStyle}
            />
          </Field>

          <div
            style={{
              gridColumn: 'span 2',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '12px 14px',
              border: `1px solid ${color.border}`,
              borderRadius: radius.lg,
              background: color.surfaceMuted,
            }}
          >
            <Toggle
              on={persona.barge_in}
              label="Barge-in permitido"
              onChange={(next) => patch((d) => { d.persona.barge_in = next })}
            />
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 600 }}>Barge-in permitido</div>
              <div style={{ fontSize: 11.5, color: color.inkSubtle }}>
                A cliente pode interromper a IA no meio da frase — a IA para de falar e escuta.
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            width: 280,
            flex: 'none',
            border: `1px solid ${color.border}`,
            borderRadius: radius.xxl,
            padding: 16,
            background: color.surface,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '.07em',
              color: color.inkSubtle,
              marginBottom: 10,
            }}
          >
            COMO SOA
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 2, height: 56, marginBottom: 12 }}
            aria-hidden
          >
            {Array.from({ length: 44 }, (_, i) => (
              <span
                key={i}
                style={{
                  flex: 1,
                  borderRadius: 2,
                  background: color.brand,
                  opacity: 0.75,
                  height: 14 + Math.round(Math.abs(Math.sin(i * 0.7) * 30 + Math.sin(i * 1.9) * 14)),
                }}
              />
            ))}
          </div>
          <div
            style={{
              fontSize: 12.5,
              lineHeight: 1.55,
              padding: '10px 12px',
              background: color.brandTint,
              borderRadius: 9,
              borderLeft: `3px solid ${color.brand}`,
            }}
          >
            {persona.greeting
              .replace('{{nome}}', 'Maria Fernandes')
              .replace('{{agente}}', persona.name)}
          </div>
        </div>
      </div>
    </div>
  )
}

// ------------------------------------------------------------------- 2 · Dados --

export function DataSection({ policy, patch }: SectionProps) {
  return (
    <div>
      <SectionHeader
        number={2}
        title="Contexto e mapeamento de dados"
        description="O que sai do core do banco e o que o agente enxerga. Campo não exposto não entra no prompt — princípio de minimização (LGPD art. 6º, III)."
      />
      <div
        style={{
          border: `1px solid ${color.border}`,
          borderRadius: radius.xxl,
          overflow: 'hidden',
          background: color.surface,
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 1fr 1fr 96px',
            background: color.brandTint,
            fontSize: 10.5,
            fontWeight: 700,
            color: color.brandInk,
            letterSpacing: '.05em',
          }}
        >
          <div style={{ padding: '10px 14px' }}>CAMPO NO CORE</div>
          <div style={{ padding: '10px 14px' }}>VARIÁVEL DO AGENTE</div>
          <div style={{ padding: '10px 14px' }}>VALOR (EXEMPLO)</div>
          <div style={{ padding: '10px 14px', textAlign: 'right' }}>EXPOR</div>
        </div>
        {policy.data_mapping.map((mapping, index) => (
          <div
            key={mapping.core}
            style={{
              display: 'grid',
              gridTemplateColumns: '1.5fr 1fr 1fr 96px',
              borderTop: `1px solid ${color.borderSoft}`,
              alignItems: 'center',
              background: mapping.exposed ? color.surface : color.surfaceMuted,
            }}
          >
            <div
              style={{
                padding: '11px 14px',
                fontFamily: "'Roboto Mono', monospace",
                fontSize: 11.5,
                color: color.inkSubtle,
              }}
            >
              {mapping.core}
            </div>
            <div
              style={{
                padding: '11px 14px',
                fontFamily: "'Roboto Mono', monospace",
                fontSize: 11.5,
                fontWeight: 500,
                color: mapping.exposed ? color.brandHover : color.inkFaint,
              }}
            >
              {mapping.exposed ? mapping.variable : '—'}
            </div>
            <div
              style={{
                padding: '11px 14px',
                fontSize: 12,
                fontVariantNumeric: 'tabular-nums',
                color: mapping.exposed ? color.ink : color.inkFaint,
              }}
            >
              {mapping.sample_value}
            </div>
            <div style={{ padding: '11px 14px', display: 'flex', justifyContent: 'flex-end' }}>
              <Toggle
                on={mapping.exposed}
                label={`Expor ${mapping.core}`}
                onChange={(next) => patch((d) => { d.data_mapping[index].exposed = next })}
              />
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'flex-start',
          marginTop: 12,
          fontSize: 12,
          color: color.purpleInk,
          background: color.purpleTint,
          border: `1px solid ${color.purpleBorder}`,
          borderRadius: radius.lg,
          padding: '11px 14px',
          lineHeight: 1.5,
        }}
      >
        <span style={{ flex: 'none', marginTop: 1 }}>
          <Shield size={15} />
        </span>
        Garantias e endereço permanecem fora do contexto do agente: não são necessários para
        negociar e ampliariam o risco em caso de vazamento.
      </div>
    </div>
  )
}

// ------------------------------------------------------------------ 3 · Matriz --

export function MatrixSection({ policy, patch, impact }: SectionProps) {
  const applied = policy.matrix.find((row) => row.applied) ?? policy.matrix[0]
  const step = policy.negotiation.concession_step

  const openings = [
    {
      key: 'teto' as const,
      label: 'Começar no teto',
      note: 'Fecha rápido e entrega toda a margem disponível na primeira frase.',
    },
    {
      key: 'meio' as const,
      label: 'Meio da faixa',
      note: `Abre em ${Math.round(applied.installment_cap / 2)}% e guarda espaço para duas rodadas.`,
    },
    {
      key: 'abaixo' as const,
      label: 'Um passo abaixo do teto',
      note: `Abre em ${Math.max(0, applied.installment_cap - step)}% e cede até ${applied.installment_cap}% só sob pressão. Recomendado.`,
    },
  ]

  return (
    <div>
      <SectionHeader
        number={3}
        title="Política de negociação — a matriz"
        description="O teto de desconto não é um número: é função de segmento, faixa de atraso e modalidade de pagamento. A IA nunca oferece um valor que não venha de uma linha desta tabela."
        badge="SEÇÃO PRINCIPAL"
      />

      <div
        style={{
          border: `1px solid ${color.border}`,
          borderRadius: radius.xxl,
          overflow: 'hidden',
          background: color.surface,
          marginBottom: 8,
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr .9fr .8fr 1.1fr .9fr .8fr .9fr',
            background: color.brandTint,
            fontSize: 10.5,
            fontWeight: 700,
            color: color.brandInk,
            letterSpacing: '.04em',
          }}
        >
          {['SEGMENTO', 'FAIXA DE ATRASO', 'TETO À VISTA', 'TETO ENTRADA+PARC.', 'ENTRADA MÍN.', 'MÁX. PARCELAS', 'PARCELA MÍN.'].map(
            (heading) => (
              <div key={heading} style={{ padding: '10px 12px' }}>
                {heading}
              </div>
            ),
          )}
        </div>

        {policy.matrix.map((row, index) => (
          <div
            key={`${row.segment}-${row.bucket}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr .9fr .8fr 1.1fr .9fr .8fr .9fr',
              borderTop: `1px solid ${row.applied ? color.greenBorder : color.borderSoft}`,
              alignItems: 'center',
              background: row.applied ? color.greenTint : color.surface,
            }}
          >
            <div
              style={{
                padding: '9px 12px',
                fontSize: 12.5,
                fontWeight: row.applied ? 700 : 500,
                color: row.applied ? color.greenInk : color.ink,
              }}
            >
              {row.segment}
            </div>
            <div
              style={{
                padding: '9px 12px',
                fontSize: 12.5,
                fontWeight: row.applied ? 700 : 500,
                color: row.applied ? color.greenInk : color.ink,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {row.bucket}
            </div>
            <div style={{ padding: '6px 8px' }}>
              <input
                value={pct(row.cash_cap)}
                aria-label={`Teto à vista ${row.segment} ${row.bucket}`}
                onChange={(e) =>
                  patch((d) => { d.matrix[index].cash_cap = parseNumber(e.target.value) })
                }
                style={cellInput(row.applied)}
              />
            </div>
            <div style={{ padding: '6px 8px' }}>
              <input
                value={pct(row.installment_cap)}
                aria-label={`Teto entrada+parcelas ${row.segment} ${row.bucket}`}
                onChange={(e) =>
                  patch((d) => { d.matrix[index].installment_cap = parseNumber(e.target.value) })
                }
                style={cellInput(row.applied)}
              />
            </div>
            <div style={{ padding: '6px 8px' }}>
              <input
                value={row.min_down_payment === null ? '—' : pct(row.min_down_payment)}
                aria-label={`Entrada mínima ${row.segment} ${row.bucket}`}
                onChange={(e) =>
                  patch((d) => {
                    // Sem dígito (ex.: "—") = sem entrada mínima na linha.
                    d.matrix[index].min_down_payment = /\d/.test(e.target.value)
                      ? parseNumber(e.target.value)
                      : null
                  })
                }
                style={cellInput(row.applied)}
              />
            </div>
            <div style={{ padding: '6px 8px' }}>
              <input
                value={`${row.max_installments}x`}
                aria-label={`Máximo de parcelas ${row.segment} ${row.bucket}`}
                onChange={(e) =>
                  patch((d) => {
                    d.matrix[index].max_installments = Math.max(1, Math.round(parseNumber(e.target.value)))
                  })
                }
                style={cellInput(row.applied)}
              />
            </div>
            <div style={{ padding: '6px 8px' }}>
              <input
                value={
                  row.min_installment_pct
                    ? `${pct(row.min_installment_pct)} da dívida`
                    : brl(row.min_installment_value)
                }
                aria-label={`Parcela mínima ${row.segment} ${row.bucket}`}
                title="Valor em R$, ou termine com % para proporcional à dívida (ex.: 1,5%)"
                onChange={(e) =>
                  patch((d) => {
                    // "R$ 150" = valor absoluto; "1,5%" = proporcional à dívida.
                    const value = e.target.value
                    if (value.includes('%')) {
                      d.matrix[index].min_installment_pct = parseNumber(value)
                    } else {
                      d.matrix[index].min_installment_pct = null
                      d.matrix[index].min_installment_value = parseNumber(value)
                    }
                  })
                }
                style={cellInput(row.applied)}
              />
            </div>
          </div>
        ))}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 12px',
            borderTop: `1px solid ${color.borderSoft}`,
            background: color.redTint,
            fontSize: 12,
            fontWeight: 600,
            color: color.redDeep,
            flexWrap: 'wrap',
          }}
        >
          <span>90+ dias · qualquer segmento</span>
          <span style={{ color: color.redInk }}>
            → negociação bloqueada, escalonar para a mesa de recuperação
          </span>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          fontSize: 11.5,
          fontWeight: 600,
          color: color.greenInk,
          marginBottom: 20,
        }}
      >
        <Check size={14} />
        Linha destacada: <b>aplicada ao caso em demonstração</b> — {applied.segment} ·{' '}
        {applied.bucket}
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div
          style={{
            flex: 1,
            minWidth: 380,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div
            style={{
              border: `1px solid ${color.border}`,
              borderRadius: radius.xl,
              padding: '14px 16px',
              background: color.surface,
            }}
          >
            <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 4 }}>
              Abertura da negociação
            </div>
            <div style={{ fontSize: 11.5, color: color.inkSubtle, marginBottom: 10 }}>
              Onde a IA começa dentro da faixa autorizada.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {openings.map((option) => {
                const selected = policy.negotiation.opening === option.key
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => patch((d) => { d.negotiation.opening = option.key })}
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                      padding: '10px 12px',
                      borderRadius: 9,
                      cursor: 'pointer',
                      border: `1.5px solid ${selected ? color.brand : color.border}`,
                      background: selected ? color.brandTint : color.surface,
                      textAlign: 'left',
                      width: '100%',
                    }}
                  >
                    <span
                      style={{
                        width: 15,
                        height: 15,
                        borderRadius: '50%',
                        border: `2px solid ${selected ? color.brand : color.inkGhost}`,
                        flex: 'none',
                        marginTop: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          background: selected ? color.brand : 'transparent',
                        }}
                      />
                    </span>
                    <span>
                      <span style={{ fontSize: 12.5, fontWeight: 600, display: 'block' }}>
                        {option.label}
                      </span>
                      <span
                        style={{ fontSize: 11.5, color: color.inkSubtle, lineHeight: 1.45 }}
                      >
                        {option.note}
                      </span>
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          <div
            style={{
              border: `1px solid ${color.border}`,
              borderRadius: radius.xl,
              padding: '14px 16px',
              background: color.surface,
              display: 'flex',
              gap: 14,
              flexWrap: 'wrap',
            }}
          >
            <Field label="Passo de concessão" style={{ flex: 1, minWidth: 130 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  value={String(policy.negotiation.concession_step).replace('.', ',')}
                  onChange={(e) =>
                    patch((d) => { d.negotiation.concession_step = parseNumber(e.target.value) })
                  }
                  style={{ ...inputStyle, width: 64, textAlign: 'center', fontWeight: 600 }}
                />
                <span style={{ fontSize: 12, color: color.inkSubtle }}>p.p. por rodada</span>
              </div>
            </Field>
            <Field label="Rodadas antes de fixar posição" style={{ flex: 1, minWidth: 130 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  value={String(policy.negotiation.rounds_before_final)}
                  onChange={(e) =>
                    patch((d) => {
                      d.negotiation.rounds_before_final = Math.max(1, parseNumber(e.target.value))
                    })
                  }
                  style={{ ...inputStyle, width: 64, textAlign: 'center', fontWeight: 600 }}
                />
                <span style={{ fontSize: 12, color: color.inkSubtle }}>rodadas</span>
              </div>
            </Field>
          </div>

          <div
            style={{
              border: `1px solid ${color.border}`,
              borderRadius: radius.xl,
              padding: '14px 16px',
              background: color.surface,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <Toggle
              on={policy.negotiation.golden_rule}
              label="Regra de ouro"
              onChange={(next) => patch((d) => { d.negotiation.golden_rule = next })}
            />
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 700 }}>
                Regra de ouro: nunca abrir no teto
              </div>
              <div style={{ fontSize: 11.5, color: color.inkSubtle, lineHeight: 1.45 }}>
                A primeira oferta fica um passo abaixo do limite. Preserva margem em toda chamada
                que fecha sem pressão.
              </div>
            </div>
          </div>

          <Field label="Regras rígidas adicionais — uma por linha">
            <textarea
              value={policy.extra_hard_rules ?? ''}
              onChange={(e) => patch((d) => { d.extra_hard_rules = e.target.value })}
              rows={3}
              placeholder={
                'Cada linha vira uma REGRA RÍGIDA no prompt, com a mesma força da regra de ouro.\n'
                + 'Ex.: Nunca parcele em mais de 12x sem entrada.'
              }
              style={{ ...inputStyle, lineHeight: 1.5, resize: 'vertical' }}
            />
          </Field>
        </div>

        <div
          style={{
            width: 340,
            flex: 'none',
            border: `1px solid ${color.border}`,
            borderRadius: radius.xxl,
            overflow: 'hidden',
            background: color.surface,
          }}
        >
          <div style={{ background: color.panel, color: '#fff', padding: '12px 16px' }}>
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '.08em',
                color: color.brandLight,
              }}
            >
              PRÉVIA DE IMPACTO · AO VIVO
            </div>
            <div style={{ fontSize: 12, color: color.panelTextSoft, marginTop: 3 }}>
              Maria Fernandes · entrada + parcelas
            </div>
          </div>
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div style={{ fontSize: 11.5, color: color.inkSubtle }}>Oferta inicial da IA</div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '-.3px',
                }}
              >
                {brl(impact.opening_value)}
              </div>
              <div
                style={{
                  fontSize: 11.5,
                  color: color.inkSubtle,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                desconto de {pct(impact.opening_pct)}
              </div>
            </div>
            <div style={{ height: 1, background: color.border }} />
            <div>
              <div style={{ fontSize: 11.5, color: color.inkSubtle }}>
                Piso da política (teto de desconto)
              </div>
              <div
                style={{ fontSize: 18, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
              >
                {brl(impact.floor_value)}
              </div>
              <div
                style={{
                  fontSize: 11.5,
                  color: color.inkSubtle,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                desconto de {pct(impact.floor_pct)}
              </div>
            </div>
            <div
              style={{
                padding: '11px 13px',
                borderRadius: radius.lg,
                background: impact.margin_preserved > 0 ? color.greenTintSoft : color.amberTint,
                border: `1px solid ${impact.margin_preserved > 0 ? color.greenBorder : color.brandBorder}`,
              }}
            >
              <div
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: impact.margin_preserved > 0 ? color.greenInk : color.amberInk,
                }}
              >
                Margem preservada vs. teto
              </div>
              <div
                style={{
                  fontSize: 19,
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  color: impact.margin_preserved > 0 ? color.greenInk : color.amberInk,
                }}
              >
                {brl(impact.margin_preserved)}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: impact.margin_preserved > 0 ? color.greenInk : color.amberInk,
                  opacity: 0.85,
                  marginTop: 3,
                }}
              >
                {impact.note}
              </div>
            </div>
            <div style={{ fontSize: 11, color: color.inkFaint, lineHeight: 1.5 }}>
              Via à vista, o mesmo caso autorizaria {brl(impact.cash_value)} ({pct(impact.cash_pct)})
              — é isso que a IA oferece quando a cliente pede mais desconto.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function cellInput(applied: boolean): React.CSSProperties {
  return {
    width: '100%',
    padding: '7px 9px',
    border: `1px solid ${applied ? '#9ed3b5' : color.border}`,
    borderRadius: 7,
    fontSize: 12.5,
    fontWeight: 600,
    textAlign: 'center',
    fontVariantNumeric: 'tabular-nums',
    background: applied ? color.surface : color.surfaceMuted,
  }
}

// -------------------------------------------------------------- 4 · Guardrails --

export function GuardrailsSection({ policy, patch }: SectionProps) {
  return (
    <div>
      <SectionHeader
        number={4}
        title="Guardrails — proibições absolutas"
        description="Regras rígidas. Entram no prompt como proibição e são reavaliadas a cada turno no Modo Auditor da tela de chamada."
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 820 }}>
        {policy.guardrails.map((guardrail, index) => (
          <div
            key={guardrail.key}
            style={{
              border: `1px solid ${color.border}`,
              borderRadius: radius.xl,
              padding: '13px 15px',
              background: color.surface,
              display: 'flex',
              gap: 12,
              alignItems: 'flex-start',
            }}
          >
            <div style={{ marginTop: 2 }}>
              <Toggle
                on={guardrail.enabled}
                label={guardrail.label}
                onChange={(next) => patch((d) => { d.guardrails[index].enabled = next })}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: guardrail.enabled ? color.ink : color.inkFaint,
                }}
              >
                {guardrail.label}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: color.inkSubtle,
                  marginTop: 5,
                  lineHeight: 1.5,
                  fontStyle: 'italic',
                }}
              >
                A IA responderia: “{guardrail.example}”
              </div>
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: 18,
          maxWidth: 820,
          border: `1px solid ${color.brandBorder}`,
          borderRadius: radius.xl,
          background: color.brandTint,
          padding: '15px 16px',
        }}
      >
        <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>Frase de contingência</div>
        <div style={{ fontSize: 11.5, color: color.brandInk, marginBottom: 9 }}>
          Usada sempre que a IA encontra algo fora da política e não pode improvisar.
        </div>
        <textarea
          value={policy.contingency_phrase}
          onChange={(e) => patch((d) => { d.contingency_phrase = e.target.value })}
          rows={3}
          style={{
            ...inputStyle,
            border: '1px solid #e6d3b6',
            borderRadius: 9,
            lineHeight: 1.55,
            resize: 'vertical',
          }}
        />
      </div>
    </div>
  )
}

// ----------------------------------------------------------- 5 · Escalonamento --

export function EscalationSection({ policy, patch }: SectionProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  return (
    <div>
      <SectionHeader
        number={5}
        title="Gatilhos de escalonamento"
        description="Quando a IA para de negociar. Cada linha expande com as frases de treino e a resposta padrão do agente."
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 14,
          padding: '12px 14px',
          border: `1px solid ${color.border}`,
          borderRadius: radius.xl,
          background: color.surface,
          maxWidth: 900,
        }}
      >
        <Toggle
          on={policy.warm_transfer}
          label="Transferência a quente"
          onChange={(next) => patch((d) => { d.warm_transfer = next })}
        />
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 700 }}>Transferência a quente</div>
          <div style={{ fontSize: 11.5, color: color.inkSubtle }}>
            {policy.warm_transfer
              ? 'O humano recebe o resumo da conversa antes de assumir a linha.'
              : 'Transferência a frio: o humano começa sem contexto.'}
          </div>
        </div>
      </div>

      <div
        style={{
          border: `1px solid ${color.border}`,
          borderRadius: radius.xxl,
          overflow: 'hidden',
          background: color.surface,
          maxWidth: 900,
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 1fr 1.2fr 1fr 30px',
            background: color.brandTint,
            fontSize: 10.5,
            fontWeight: 700,
            color: color.brandInk,
            letterSpacing: '.05em',
          }}
        >
          <div style={{ padding: '10px 14px' }}>GATILHO</div>
          <div style={{ padding: '10px 14px' }}>DETECÇÃO</div>
          <div style={{ padding: '10px 14px' }}>AÇÃO</div>
          <div style={{ padding: '10px 14px' }}>DESTINO</div>
          <div />
        </div>

        {policy.escalations.map((escalation) => {
          const open = Boolean(expanded[escalation.key])
          return (
            <div key={escalation.key} style={{ borderTop: `1px solid ${color.borderSoft}` }}>
              <button
                type="button"
                onClick={() => setExpanded((c) => ({ ...c, [escalation.key]: !c[escalation.key] }))}
                aria-expanded={open}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.5fr 1fr 1.2fr 1fr 30px',
                  alignItems: 'center',
                  cursor: 'pointer',
                  background: open ? color.surfaceMuted : color.surface,
                  border: 'none',
                  width: '100%',
                  textAlign: 'left',
                  padding: 0,
                }}
              >
                <div style={{ padding: '11px 14px', fontSize: 12.5, fontWeight: 600 }}>
                  {escalation.trigger}
                </div>
                <div style={{ padding: '11px 14px', fontSize: 11.5, color: color.inkSubtle }}>
                  {escalation.detection}
                </div>
                <div
                  style={{
                    padding: '11px 14px',
                    fontSize: 12,
                    color: escalation.hard ? color.redInk : color.ink,
                    fontWeight: 500,
                  }}
                >
                  {escalation.action}
                </div>
                <div style={{ padding: '11px 14px', fontSize: 12 }}>{escalation.destination}</div>
                <div style={{ fontSize: 11, color: color.inkFaint }} aria-hidden>
                  {open ? '▾' : '▸'}
                </div>
              </button>

              {open ? (
                <div
                  style={{
                    padding: '12px 16px 14px',
                    background: color.surfaceMuted,
                    borderTop: `1px dashed ${color.border}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: color.inkSubtle,
                      marginBottom: 6,
                    }}
                  >
                    FRASES DE TREINO
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                    {escalation.phrases.map((phrase) => (
                      <span
                        key={phrase}
                        style={{
                          fontSize: 11.5,
                          padding: '5px 10px',
                          borderRadius: radius.pill,
                          background: color.surface,
                          border: `1px solid ${color.border}`,
                          color: color.inkSubtle,
                        }}
                      >
                        “{phrase}”
                      </span>
                    ))}
                  </div>
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: color.inkSubtle,
                      marginBottom: 5,
                    }}
                  >
                    RESPOSTA PADRÃO DO AGENTE
                  </div>
                  <div
                    style={{
                      fontSize: 12.5,
                      lineHeight: 1.55,
                      padding: '10px 12px',
                      background: color.brandTint,
                      borderLeft: `3px solid ${color.brand}`,
                      borderRadius: radius.md,
                    }}
                  >
                    {escalation.reply}
                  </div>
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ------------------------------------------------------------- 6 · Ferramentas --

export function ToolsSection({ policy, patch }: SectionProps) {
  return (
    <div>
      <SectionHeader
        number={6}
        title="Ferramentas que o agente pode executar"
        description="Cada ferramenta gera um artefato verificável. Sem artefato em tela, a IA não pode dizer que enviou."
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(240px, 1fr))',
          gap: 12,
          maxWidth: 1100,
        }}
      >
        {policy.tools.map((tool, index) => (
          <div
            key={tool.name}
            style={{
              border: `1px solid ${color.border}`,
              borderRadius: radius.xl,
              padding: 14,
              background: color.surface,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <span
                style={{
                  fontFamily: "'Roboto Mono', monospace",
                  fontSize: 12,
                  fontWeight: 500,
                  color: tool.enabled ? color.blueInk : color.inkFaint,
                  flex: 1,
                }}
              >
                {tool.name}
              </span>
              <Toggle
                on={tool.enabled}
                label={`Habilitar ${tool.name}`}
                onChange={(next) => patch((d) => { d.tools[index].enabled = next })}
              />
            </div>
            <div style={{ fontSize: 11.5, color: color.inkSubtle, lineHeight: 1.45 }}>
              Gera: {tool.artifact}
            </div>
            <select
              value={tool.channel}
              aria-label={`Canal de ${tool.name}`}
              onChange={(e) =>
                patch((d) => { d.tools[index].channel = e.target.value as typeof tool.channel })
              }
              style={{ ...inputStyle, fontSize: 11.5, padding: '7px 8px' }}
            >
              <option value="whatsapp">WhatsApp</option>
              <option value="sms">SMS</option>
              <option value="email">E-mail</option>
            </select>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                paddingTop: 9,
                borderTop: `1px dashed ${color.border}`,
              }}
            >
              <label
                style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11.5, cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={tool.requires_verbal_confirmation}
                  onChange={(e) =>
                    patch((d) => {
                      d.tools[index].requires_verbal_confirmation = e.target.checked
                    })
                  }
                  style={{ accentColor: color.brand }}
                />
                Exige confirmação verbal da cliente
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  fontSize: 11.5,
                  color: color.greenInk,
                  fontWeight: 600,
                }}
                title="Regra não editável"
              >
                <input type="checkbox" checked disabled style={{ accentColor: color.green }} />
                Exige confirmação visual em tela · travado
              </label>
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          marginTop: 14,
          maxWidth: 1100,
          fontSize: 12,
          color: color.redDeep,
          background: color.redTint,
          border: `1px solid ${color.redBorder}`,
          borderRadius: radius.lg,
          padding: '12px 14px',
          lineHeight: 1.55,
        }}
      >
        A IA nunca declara verbalmente que algo foi enviado sem que o artefato apareça em tela com
        mudança de status. <b>Regra não editável.</b>
      </div>
    </div>
  )
}

// ------------------------------------------------------------ 7 · Canais/cadência --

export function ChannelsSection({ policy, patch, answerRate }: SectionProps) {
  const { contact_window: window } = policy
  const maxRate = Math.max(...answerRate, 1)
  const peakIndex = answerRate.indexOf(maxRate)

  return (
    <div>
      <SectionHeader
        number={7}
        title="Canais e cadência"
        description="Quando pode ligar, com que intervalo, e o que fazer quando ninguém atende."
      />
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div
          style={{
            border: `1px solid ${color.border}`,
            borderRadius: radius.xxl,
            padding: 16,
            background: color.surface,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '.07em',
              color: color.inkSubtle,
              marginBottom: 12,
            }}
          >
            JANELA PERMITIDA
          </div>
          <div style={{ display: 'flex', gap: 3 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginRight: 4 }}>
              <div style={{ height: 16 }} />
              {window.hours.map((hour) => (
                <div
                  key={hour}
                  style={{
                    height: 15,
                    fontSize: 9.5,
                    color: color.inkFaint,
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: '15px',
                  }}
                >
                  {hour}
                </div>
              ))}
            </div>
            {window.days.map((day, dayIndex) => (
              <div key={day} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div
                  style={{
                    height: 16,
                    fontSize: 10,
                    fontWeight: 700,
                    color: color.inkSubtle,
                    textAlign: 'center',
                  }}
                >
                  {day}
                </div>
                {window.hours.map((hour, hourIndex) => {
                  const allowed = window.grid[dayIndex]?.[hourIndex] ?? false
                  return (
                    <button
                      key={hour}
                      type="button"
                      title={`${day} ${hour} · ${allowed ? 'permitido' : 'bloqueado'}`}
                      aria-label={`${day} ${hour} ${allowed ? 'permitido' : 'bloqueado'}`}
                      onClick={() =>
                        patch((d) => {
                          d.contact_window.grid[dayIndex][hourIndex] = !allowed
                        })
                      }
                      style={{
                        width: 26,
                        height: 15,
                        borderRadius: 3,
                        cursor: 'pointer',
                        transition: 'background .15s ease',
                        background: allowed ? color.green : color.bg,
                        border: 'none',
                        padding: 0,
                      }}
                    />
                  )
                })}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 14, marginTop: 12, fontSize: 11, color: color.inkSubtle }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 11, height: 11, borderRadius: 3, background: color.green }} />
              permitido
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 11, height: 11, borderRadius: 3, background: color.bg }} />
              bloqueado
            </span>
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 300, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div
            style={{
              border: `1px solid ${color.border}`,
              borderRadius: radius.xxl,
              padding: 16,
              background: color.surface,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '.07em',
                color: color.inkSubtle,
                marginBottom: 12,
              }}
            >
              TAXA HISTÓRICA DE ATENDIMENTO POR HORÁRIO
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 96 }}>
              {answerRate.map((rate, index) => {
                const strong = rate >= 33
                return (
                  <div
                    key={index}
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 5,
                    }}
                    title={`${window.hours[index]} · ${rate}% de atendimento`}
                  >
                    <div
                      style={{
                        width: '100%',
                        borderRadius: '3px 3px 0 0',
                        transition: 'height .3s ease',
                        background: strong ? color.green : rate >= 22 ? '#8fcbaa' : '#dfe4ea',
                        height: Math.round((rate / 45) * 96),
                      }}
                    />
                    <span
                      style={{
                        fontSize: 9.5,
                        color: strong ? color.greenInk : color.inkFaint,
                        fontWeight: strong ? 700 : 400,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {window.hours[index]}
                    </span>
                  </div>
                )
              })}
            </div>
            <div
              style={{ fontSize: 11.5, color: color.greenInk, fontWeight: 600, marginTop: 10 }}
            >
              Pico {window.hours[peakIndex]}–{window.hours[peakIndex + 2] ?? ''} · {maxRate}% de
              atendimento — a janela em que a operação humana está saturada.
            </div>
          </div>

          <div
            style={{
              border: `1px solid ${color.border}`,
              borderRadius: radius.xxl,
              padding: 16,
              background: color.surface,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 14,
            }}
          >
            <Field label="Intervalo mínimo entre tentativas">
              <input
                value={`${window.min_interval_hours} horas`}
                onChange={(e) =>
                  patch((d) => {
                    d.contact_window.min_interval_hours = Math.max(1, parseNumber(e.target.value))
                  })
                }
                style={inputStyle}
              />
            </Field>
            <Field label="Máx. tentativas por dia">
              <input
                value={String(window.max_attempts_per_day)}
                onChange={(e) =>
                  patch((d) => {
                    d.contact_window.max_attempts_per_day = Math.max(1, parseNumber(e.target.value))
                  })
                }
                style={inputStyle}
              />
            </Field>
            <Field label="Máx. tentativas por semana">
              <input
                value={String(window.max_attempts_per_week)}
                onChange={(e) =>
                  patch((d) => {
                    d.contact_window.max_attempts_per_week = Math.max(1, parseNumber(e.target.value))
                  })
                }
                style={inputStyle}
              />
            </Field>
            <Field label="Fallback quando não atende">
              <input
                value={window.fallback}
                onChange={(e) => patch((d) => { d.contact_window.fallback = e.target.value })}
                style={inputStyle}
              />
            </Field>
          </div>
        </div>
      </div>
    </div>
  )
}

// -------------------------------------------------------------- 8 · Compliance --

export function ComplianceSection({ policy, patch }: SectionProps) {
  const { compliance } = policy
  return (
    <div>
      <SectionHeader
        number={8}
        title="Compliance e LGPD"
        description="O que sustenta a operação diante do regulador e da auditoria interna."
      />
      <div
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, maxWidth: 960 }}
      >
        <div
          style={{
            border: `1px solid ${color.border}`,
            borderRadius: radius.xl,
            padding: 16,
            background: color.surface,
          }}
        >
          <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 10 }}>
            Base legal do tratamento
          </div>
          <select
            value={compliance.legal_basis}
            onChange={(e) => patch((d) => { d.compliance.legal_basis = e.target.value })}
            style={inputStyle}
          >
            <option>Execução de contrato (LGPD art. 7º, V)</option>
            <option>Legítimo interesse (art. 7º, IX)</option>
          </select>
          <div
            style={{ fontSize: 11.5, color: color.inkSubtle, marginTop: 10, lineHeight: 1.5 }}
          >
            Cobrança de dívida decorrente de contrato vigente — não depende de consentimento, mas o
            aviso de gravação é obrigatório.
          </div>
        </div>

        <div
          style={{
            border: `1px solid ${color.border}`,
            borderRadius: radius.xl,
            padding: 16,
            background: color.surface,
          }}
        >
          <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 10 }}>
            Aviso de gravação (primeiros segundos)
          </div>
          <textarea
            rows={3}
            value={compliance.recording_notice}
            onChange={(e) => patch((d) => { d.compliance.recording_notice = e.target.value })}
            style={{ ...inputStyle, fontSize: 12.5, lineHeight: 1.5, resize: 'vertical' }}
          />
        </div>

        <div
          style={{
            border: `1px solid ${color.border}`,
            borderRadius: radius.xl,
            padding: 16,
            background: color.surface,
          }}
        >
          <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 10 }}>Retenção</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 12.5 }}>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
            >
              Áudio da chamada
              <input
                value={compliance.audio_retention}
                onChange={(e) => patch((d) => { d.compliance.audio_retention = e.target.value })}
                style={{ ...inputStyle, width: 110, padding: '7px 9px', textAlign: 'center' }}
              />
            </div>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
            >
              Transcrição e trilha
              <input
                value={compliance.transcript_retention}
                onChange={(e) =>
                  patch((d) => { d.compliance.transcript_retention = e.target.value })
                }
                style={{ ...inputStyle, width: 110, padding: '7px 9px', textAlign: 'center' }}
              />
            </div>
          </div>
          <div
            style={{
              marginTop: 12,
              padding: '10px 12px',
              background: color.surfaceMuted,
              border: `1px solid ${color.border}`,
              borderRadius: 9,
              fontSize: 12,
            }}
          >
            Mascaramento de PII no log:{' '}
            <span style={{ fontFamily: "'Roboto Mono', monospace", fontWeight: 500 }}>
              {compliance.pii_mask_example}
            </span>
          </div>
        </div>

        <div
          style={{
            border: `1px solid ${color.greenBorder}`,
            borderRadius: radius.xl,
            padding: 16,
            background: color.greenTintSoft,
          }}
        >
          <div
            style={{ fontSize: 12.5, fontWeight: 700, color: color.greenInk, marginBottom: 10 }}
          >
            Trilha de auditoria
          </div>
          <div style={{ fontSize: 12, color: color.greenInk, lineHeight: 1.6 }}>
            Cada decisão do agente é registrada com carimbo de tempo, versão da política aplicada e
            hash do payload — append-only, exportável em JSON e CSV. É o que responde “prove o que a
            máquina fez”.
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- 9 · Personas --

export function PersonasSection({ scenarios }: { scenarios: { id: string; label: string; description: string; case_id: string }[] }) {
  const TINTS = [
    { bg: color.greenTint, fg: color.greenInk },
    { bg: color.brandTint, fg: color.brandInk },
    { bg: color.redTint, fg: color.redInk },
    { bg: color.purpleTint, fg: color.purpleInk },
    { bg: color.blueTint, fg: color.blueInk },
  ]

  return (
    <div>
      <SectionHeader
        number={9}
        title="Personas de teste"
        description="Cenários prontos para rodar em sandbox antes de publicar. Cada um exercita um caminho diferente da política."
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(250px, 1fr))',
          gap: 14,
          maxWidth: 1000,
        }}
      >
        {scenarios.map((scenario, index) => {
          const tint = TINTS[index % TINTS.length]
          return (
            <div
              key={scenario.id}
              style={{
                border: `1px solid ${color.border}`,
                borderRadius: radius.xxl,
                padding: 16,
                background: color.surface,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: radius.xl,
                    background: tint.bg,
                    color: tint.fg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: 14,
                    flex: 'none',
                  }}
                >
                  {scenario.id}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>{scenario.label}</div>
                </div>
              </div>
              <div style={{ fontSize: 12, color: color.inkSubtle, lineHeight: 1.5 }}>
                {scenario.description}
              </div>
              <a
                href={`${withBase("")}?cenario=${scenario.id}`}
                style={{
                  marginTop: 'auto',
                  textAlign: 'center',
                  border: `1.5px solid ${color.brand}`,
                  color: color.brandHover,
                  borderRadius: 9,
                  padding: 9,
                  fontSize: 12.5,
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                Executar em sandbox
              </a>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export { Button }
